/** Growable little-endian binary writer. */
export class ByteWriter {
  private buf: ArrayBuffer;
  private view: DataView;
  private u8: Uint8Array;
  pos = 0;
  constructor(initial = 256) {
    this.buf = new ArrayBuffer(initial);
    this.view = new DataView(this.buf);
    this.u8 = new Uint8Array(this.buf);
  }
  private ensure(n: number) {
    if (this.pos + n <= this.buf.byteLength) return;
    let size = this.buf.byteLength * 2;
    while (size < this.pos + n) size *= 2;
    const nb = new ArrayBuffer(size);
    new Uint8Array(nb).set(this.u8);
    this.buf = nb;
    this.view = new DataView(nb);
    this.u8 = new Uint8Array(nb);
  }
  u8w(v: number) {
    this.ensure(1);
    this.view.setUint8(this.pos, v);
    this.pos += 1;
    return this;
  }
  i8w(v: number) {
    this.ensure(1);
    this.view.setInt8(this.pos, v);
    this.pos += 1;
    return this;
  }
  u16w(v: number) {
    this.ensure(2);
    this.view.setUint16(this.pos, v, true);
    this.pos += 2;
    return this;
  }
  i16w(v: number) {
    this.ensure(2);
    this.view.setInt16(this.pos, v, true);
    this.pos += 2;
    return this;
  }
  u32w(v: number) {
    this.ensure(4);
    this.view.setUint32(this.pos, v >>> 0, true);
    this.pos += 4;
    return this;
  }
  i32w(v: number) {
    this.ensure(4);
    this.view.setInt32(this.pos, v | 0, true);
    this.pos += 4;
    return this;
  }
  f32w(v: number) {
    this.ensure(4);
    this.view.setFloat32(this.pos, v, true);
    this.pos += 4;
    return this;
  }
  f64w(v: number) {
    this.ensure(8);
    this.view.setFloat64(this.pos, v, true);
    this.pos += 8;
    return this;
  }
  strw(s: string) {
    const bytes = new TextEncoder().encode(s);
    if (bytes.length > 65535) throw new Error('string too long');
    this.u16w(bytes.length);
    this.ensure(bytes.length);
    this.u8.set(bytes, this.pos);
    this.pos += bytes.length;
    return this;
  }
  bytes(): Uint8Array {
    return this.u8.slice(0, this.pos);
  }
}

export class ByteReader {
  private view: DataView;
  private u8: Uint8Array;
  pos = 0;
  constructor(data: ArrayBuffer | Uint8Array) {
    this.u8 = data instanceof Uint8Array ? data : new Uint8Array(data);
    this.view = new DataView(this.u8.buffer, this.u8.byteOffset, this.u8.byteLength);
  }
  get remaining() {
    return this.u8.byteLength - this.pos;
  }
  u8r() {
    const v = this.view.getUint8(this.pos);
    this.pos += 1;
    return v;
  }
  i8r() {
    const v = this.view.getInt8(this.pos);
    this.pos += 1;
    return v;
  }
  u16r() {
    const v = this.view.getUint16(this.pos, true);
    this.pos += 2;
    return v;
  }
  i16r() {
    const v = this.view.getInt16(this.pos, true);
    this.pos += 2;
    return v;
  }
  u32r() {
    const v = this.view.getUint32(this.pos, true);
    this.pos += 4;
    return v;
  }
  i32r() {
    const v = this.view.getInt32(this.pos, true);
    this.pos += 4;
    return v;
  }
  f32r() {
    const v = this.view.getFloat32(this.pos, true);
    this.pos += 4;
    return v;
  }
  f64r() {
    const v = this.view.getFloat64(this.pos, true);
    this.pos += 8;
    return v;
  }
  strr() {
    const n = this.u16r();
    const s = new TextDecoder().decode(this.u8.subarray(this.pos, this.pos + n));
    this.pos += n;
    return s;
  }
}
